import {
  NumericFormatType,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControlType,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { EquityPortfolioModel } from './equity-portfolio.model';

export class EquityPortfolioConfiguration extends ReportSectionConfigurationBase {
  typeName = 'EquityPortfolioConfiguration';
  nodataLocalizationKeyName = 'equityPortfolio';
  readonly reportSection = ReportSection.EquityPortfolio;
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  readonly reportViewModelSections = [ReportViewModelSection.InvestorProfile_EquityPortfolio];

  get minSize(): number {
    if (
      this.availableDataSections.some(
        section =>
          ['equityPortfolioTopHoldingsGrid', 'equityPortfolioTopBuysGrid', 'equityPortfolioTopSellsGrid'].includes(
            section.name
          ) && section.isSelected
      )
    ) {
      return 2;
    }
    return 1;
  }

  maxSize = 4;
  defaultSize = 4;
  readonly availableDataSections = [
    {
      name: 'equityPortfolioValue',
      displayName: 'EQ Portfolio Value',
      isSelected: true,
      canConfigure: true,
      property: 'eqPortfolioValue'
    },
    {
      name: 'equityPortfolioHoldings',
      displayName: 'Holdings',
      isSelected: true,
      canConfigure: true,
      property: 'totalCount'
    },
    {
      name: 'equityPortfolioBuys',
      displayName: 'Buys',
      isSelected: true,
      canConfigure: true,
      property: 'buys'
    },
    {
      name: 'equityPortfolioSells',
      displayName: 'Sells',
      isSelected: true,
      canConfigure: true,
      property: 'sells'
    },
    {
      name: 'equityPortfolioUnchanged',
      displayName: 'Unchanged',
      isSelected: true,
      canConfigure: true,
      property: 'unchanged'
    },
    {
      name: 'equityPortfolioBuysValue',
      displayName: 'Buys Value USD, mm',
      isSelected: true,
      canConfigure: true,
      property: 'buysValue'
    },
    {
      name: 'equityPortfolioSellsValue',
      displayName: 'Sells Value USD, mm',
      isSelected: true,
      canConfigure: true,
      property: 'sellsValue'
    },
    {
      name: 'equityPortfolioMarketCap',
      displayName: 'Market Cap',
      isSelected: true,
      canConfigure: true,
      property: 'marketCap'
    },
    {
      name: 'equityPortfolioTopHoldingsGrid',
      displayName: 'Top Holdings',
      type: ReportSectionConfigurationControlType.Table,
      isSelected: true,
      canConfigure: true,
      subSections: [
        {
          name: 'equityPortfolioTopHoldingsGridSecurityName',
          displayName: 'Security Name',
          isSelected: true,
          canConfigure: false,
          isStatic: true,
          property: 'securityIssuerName'
        },
        {
          name: 'equityPortfolioTopHoldingsGridValue',
          displayName: 'Value USD, mm',
          isSelected: true,
          canConfigure: true,
          property: 'value',
          type: ReportSectionConfigurationControlType.Decimal
        },
        {
          name: 'equityPortfolioTopHoldingsGridPctSO',
          displayName: '% S/O',
          isSelected: false,
          canConfigure: true,
          property: 'sharesOutstandingPct'
        },
        {
          name: 'equityPortfolioTopHoldingsGridPctPortfolio',
          displayName: '% Portfolio',
          isSelected: false,
          canConfigure: true,
          property: 'portfolioPct'
        }
      ]
    },
    {
      name: 'equityPortfolioTopBuysGrid',
      displayName: 'Top Buys',
      type: ReportSectionConfigurationControlType.Table,
      isSelected: true,
      canConfigure: true,
      subSections: [
        {
          name: 'equityPortfolioTopBuysGridSecurityName',
          displayName: 'Security Name',
          isSelected: true,
          canConfigure: false,
          isStatic: true,
          property: 'securityIssuerName'
        },
        {
          name: 'equityPortfolioTopBuysGridValueChange',
          displayName: 'Share Change Value USD, mm',
          isSelected: true,
          canConfigure: true,
          property: 'sharesChangeValue',
          type: ReportSectionConfigurationControlType.Decimal,
          numericFormatType: NumericFormatType.InMillions
        },
        {
          name: 'equityPortfolioTopBuysGridPctSO',
          displayName: '% S/O',
          isSelected: false,
          canConfigure: true,
          property: 'sharesOutstandingPct',
          type: ReportSectionConfigurationControlType.Decimal,
          numericFormatType: NumericFormatType.Percentage
        },
        {
          name: 'equityPortfolioTopBuysGridPctPortfolio',
          displayName: '% Portfolio',
          isSelected: false,
          canConfigure: true,
          property: 'portfolioPct'
        }
      ]
    },
    {
      name: 'equityPortfolioTopSellsGrid',
      displayName: 'Top Sells',
      type: ReportSectionConfigurationControlType.Table,
      isSelected: true,
      canConfigure: true,
      subSections: [
        {
          name: 'equityPortfolioTopSellsGridSecurityName',
          displayName: 'Security Name',
          isSelected: true,
          canConfigure: false,
          isStatic: true,
          property: 'securityIssuerName'
        },
        {
          name: 'equityPortfolioTopSellsGridValueChange',
          displayName: 'Share Change Value USD, mm',
          isSelected: true,
          canConfigure: true,
          property: 'sharesChangeValue',
          type: ReportSectionConfigurationControlType.Decimal
        },
        {
          name: 'equityPortfolioTopSellsGridPctSO',
          displayName: '% S/O',
          isSelected: false,
          canConfigure: true,
          property: 'sharesOutstandingPct'
        },
        {
          name: 'equityPortfolioTopSellsGridPctPortfolio',
          displayName: '% Portfolio',
          isSelected: false,
          canConfigure: true,
          property: 'portfolioPct'
        }
      ]
    }
  ];

  get name(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactEquityPortfolio';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'institutionEquityPortfolio';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'Equity Portfolio';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface EquityPortfolioData {
  [ReportViewModelSection.InvestorProfile_EquityPortfolio]: EquityPortfolioModel;
}
